import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { CLUB_GENRES, PACE_BANDS, type BookSearchResult, type DirectoryClub, type PublicClub } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { EmptyState, Spinner } from "../components/ui";
import { ChevronIcon } from "../components/Icons";
import { Cover } from "../components/BookBits";
import { BookSearch } from "../components/BookSearch";
import { BackBar } from "../components/ui";
import { AskSheet, JoinSheet } from "../components/PublicClub";
import { clubs } from "@pagemates/shared/copy";

const txt = clubs.directory;

/*
 * The Directory (PLAN.md §Public clubs): Open and Request clubs, for anyone
 * signed in. Search, then filters as chips (each opens a small panel), and a
 * sort. Filters live in the URL so Back keeps them. 20 at a time.
 */
export interface DirSearch { q?: string; book?: number; bookTitle?: string; genre?: string[]; pace?: string; size?: string; join?: string; sort?: string }

const SIZES: [string, string][] = [["small", txt.smallUnder10], ["medium", txt.medium1050], ["large", txt.large50]];
const JOINS: [string, string][] = [["open", txt.open], ["request", txt.requestToJoin]];
const SORTS: [string, string][] = [["active", txt.mostActive], ["newest", txt.newest], ["members", txt.mostMembers]];
const paceLabel = (k: string | null) => PACE_BANDS.find((b) => b.key === k)?.label ?? null;

export function Directory() {
  const s = useSearch({ from: "/authed/clubs/browse" }) as DirSearch;
  const navigate = useNavigate();
  const set = (patch: Partial<DirSearch>) => void navigate({ to: "/clubs/browse", search: { ...s, ...patch }, replace: true });
  const [text, setText] = useState(s.q ?? "");
  useEffect(() => { const t = setTimeout(() => { if ((s.q ?? "") !== text.trim()) set({ q: text.trim() || undefined }); }, 400); return () => clearTimeout(t); }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  const [open, setOpen] = useState<string | null>(null);
  const q = useInfiniteQuery({
    queryKey: ["directory", s.q, s.book, s.genre, s.pace, s.size, s.join, s.sort],
    queryFn: ({ pageParam }) => api.directory({ q: s.q, book: s.book, genres: s.genre, pace: s.pace, size: s.size, join: s.join, sort: s.sort, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const total = q.data?.pages[0]?.total ?? 0;
  const genres = s.genre ?? [];
  const chip = (key: string, label: string, on: boolean) => (
    <button type="button" className={`fchip${on ? " on" : ""}`} aria-expanded={open === key} onClick={() => setOpen(open === key ? null : key)}>{label} <ChevronIcon className="fchev" width={12} height={12} /></button>
  );
  const anyFilter = Boolean(s.book || genres.length || s.pace || s.size || s.join);
  return (
    <main className="page directory">
      <BackBar label={txt.yourClubs} link={{ to: "/clubs", search: { all: true } }} />
      <h1>{txt.findAClub}</h1>
      <p className="muted small" style={{ margin: "2px 0 14px" }}>{txt.clubsThatWelcomeNew}</p>
      <input id="dir-search" className="input" type="search" placeholder={txt.searchClubsByName} value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />
      <div className="frow" role="toolbar" aria-label={txt.filters}>
        {chip("book", s.bookTitle ? `Reading ${s.bookTitle}` : "Reading now", Boolean(s.book))}
        {chip("genre", genres.length ? `Genre · ${genres.length}` : "Genre", genres.length > 0)}
        {chip("pace", s.pace ? paceLabel(s.pace)! : "Pace", Boolean(s.pace))}
        {chip("size", s.size ? SIZES.find(([k]) => k === s.size)![1].split(" · ")[0]! : "Size", Boolean(s.size))}
        {chip("join", s.join ? JOINS.find(([k]) => k === s.join)![1] : "How to join", Boolean(s.join))}
        {anyFilter && <button type="button" className="linkish small" onClick={() => set({ book: undefined, bookTitle: undefined, genre: undefined, pace: undefined, size: undefined, join: undefined })}>{txt.clear}</button>}
      </div>
      {open && (
        <div className="fpanel card">
          {open === "book" && (s.book
            ? <div className="row spread"><span>{txt.clubsReading}<b>{s.bookTitle}</b></span><button type="button" className="btn ghost sm" onClick={() => { set({ book: undefined, bookTitle: undefined }); setOpen(null); }}>{txt.anyBook}</button></div>
            : <BookSearch onPick={(b: BookSearchResult) => { set({ book: b.hardcoverId, bookTitle: b.title }); setOpen(null); }} />)}
          {open === "genre" && (
            <div className="genre-pick">
              {CLUB_GENRES.map((g) => {
                const on = genres.includes(g);
                return <button key={g} type="button" className={`gchip${on ? " on" : ""}`} aria-pressed={on} onClick={() => set({ genre: on ? genres.filter((x) => x !== g) : [...genres, g] })}>{g}</button>;
              })}
            </div>
          )}
          {open === "pace" && <Choices value={s.pace} options={PACE_BANDS.map((b) => [b.key, txt.pagesADay(b.label, b.key === "fast" ? "30+" : `${b.min}–${b.max - 1}`)])} onPick={(v) => { set({ pace: v }); setOpen(null); }} />}
          {open === "size" && <Choices value={s.size} options={SIZES} onPick={(v) => { set({ size: v }); setOpen(null); }} />}
          {open === "join" && <Choices value={s.join} options={JOINS} onPick={(v) => { set({ join: v }); setOpen(null); }} />}
        </div>
      )}
      <div className="row spread dir-meta">
        <span className="muted small">{q.isPending ? txt.looking : `${total} ${total === 1 ? txt.club : txt.clubs}`}</span>
        <label className="row small muted" style={{ gap: 6 }}>{txt.sort}<select id="dir-sort" className="input sm" value={s.sort ?? "active"} onChange={(e) => set({ sort: e.target.value === "active" ? undefined : e.target.value })}>
            {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
      </div>
      {q.isError && <p className="error">{describeError(q.error)}</p>}
      {q.isPending ? <Spinner /> : items.length === 0 ? (
        <EmptyState title={anyFilter || s.q ? txt.noClubsMatch : txt.noClubsToFind}>
          {anyFilter || s.q ? txt.tryFewerFiltersOr : txt.whenClubsChooseTo}
        </EmptyState>
      ) : (
        <div className="dgrid">{items.map((c) => <DirCard key={c.id} c={c} />)}</div>
      )}
      {q.hasNextPage && <button type="button" className="btn dir-more" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>{q.isFetchingNextPage ? txt.loading : txt.show20More}</button>}
    </main>
  );
}

function Choices({ value, options, onPick }: { value?: string; options: [string, string][]; onPick: (v: string | undefined) => void }) {
  return (
    <div className="fchoices">
      {options.map(([k, l]) => <button key={k} type="button" className={`gchip${value === k ? " on" : ""}`} aria-pressed={value === k} onClick={() => onPick(value === k ? undefined : k)}>{l}</button>)}
    </div>
  );
}

/** One club: cover, name, line, size and pace, genres, and Join / Ask (or "Member"). The card opens its page. */
function DirCard({ c }: { c: DirectoryClub }) {
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<{ kind: "join" | "ask"; club: PublicClub } | null>(null);
  const load = useMutation({ mutationFn: () => api.publicClub(c.id), onSuccess: (club) => setSheet({ kind: c.visibility === "open" ? "join" : "ask", club }) });
  const go = () => void navigate({ to: "/clubs/$clubId", params: { clubId: c.slug } });
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div className="dcard" ref={ref}>
      <button type="button" className="dcard-hit" onClick={go} aria-label={txt.openItsPage(c.name)} />
      {c.current ? <Cover book={{ hardcoverId: 0, title: c.current.title, authors: [], coverUrl: c.current.coverUrl, pages: null, releaseYear: null }} size="sm" /> : <span className="cover sm blank-cover" />}
      <div className="dc-body">
        <div className="dc-top">
          <b className="serif">{c.name}</b>
          {c.member ? <span className="pill member">{txt.member}</span>
            : <button type="button" className="btn sm dc-act" disabled={load.isPending} onClick={() => load.mutate()}>{c.visibility === "open" ? txt.join : txt.ask}</button>}
        </div>
        {c.description && <div className="muted small dc-desc">{c.description}</div>}
        <div className="small muted dc-meta">
          {c.memberCount} {c.memberCount === 1 ? txt.member2 : txt.members}{c.pace ? ` · ${paceLabel(c.pace)}` : ""}{c.current ? txt.reading(c.current.title) : ""}
          {!c.spoilerGating && <> · <span className="notice">{txt.spoilersNotHidden}</span></>}
        </div>
        {c.genres.length > 0 && <div className="pills">{c.genres.map((g) => <span key={g} className="pill">{g}</span>)}</div>}
      </div>
      {sheet?.kind === "join" && <JoinSheet c={sheet.club} onClose={() => setSheet(null)} />}
      {sheet?.kind === "ask" && <AskSheet c={sheet.club} onClose={() => setSheet(null)} />}
    </div>
  );
}
